export const namespaced = true;

import StudentImporterService from '@/services/StudentImporterService.js';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import map from 'lodash/map';
import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';

export const state = {
  students: [],
  groupStudent: {
    selectedStudents: [],
    corporation: {},
    brand: {},
    school: {},
    course: {},
    tamaiClass: {},
  },
  csvHeaderMapping: {
    no: 'index',
    uid: 'studentID',
    name: 'name',
    nameKana: 'name_kana',
    nameRomaji: 'name_romaji',
    yearOfBirth: 'birth_year',
    monthOfBirth: 'birth_month',
    genderId: 'gender',
    countryOfCitizenshipId: 'kuni',
    countryOfResidenceId: 'kuni_kyojyuu',
    schoolYearId: 'gakunen',
    address: 'address',
    password: 'pass',
    figuredTeachingMaterialCode: 'kyouzai',
    language: 'language',
    remark1: 'remark1',
    remark2: 'remark2',
    remark3: 'remark3',
    remark4: 'remark4',
    remark5: 'remark5',
    newLine: 'newline',
  },
  csvRequiredHeaders: [
    'no',
    'uid',
    'name',
    'nameKana',
    'nameRomaji',
    'yearOfBirth',
    'monthOfBirth',
    'genderId',
    'countryOfCitizenshipId',
    'countryOfResidenceId',
    'schoolYearId',
    'address',
    'password',
    'figuredTeachingMaterialCode',
    'language',
  ],
};

export const mutations = {
  SET_STUDENTS(state, students) {
    state.students = students;
  },
  SET_SELECTED_STUDENTS(state, students) {
    state.groupStudent.selectedStudents = students;
  },
  UPDATE_STUDENTS_FROM_SELECTED_STUDENTS(state, formattedStudents) {
    formattedStudents.forEach((formattedStudent) => {
      let index = state.students.findIndex((item) => item.no === formattedStudent.no);
      state.students.splice(index, 1, formattedStudent);
    });
  },
  CLEAR_SELECTED_STUDENTS(state) {
    state.groupStudent.selectedStudents = [];
  },
  REMOVE_STUDENTS(state, studentNos) {
    state.students = state.students.filter((student) => !studentNos.includes(student.no));
  },
  MARK_STUDENT_NO_ERROR(state, studentNo) {
    state.students = state.students.map((student) => {
      if (studentNo == student.no) {
        student.error = '';
      }

      return student;
    });
  },
  CLEAR_STUDENTS(state) {
    state.students = [];
    state.groupStudent = {
      selectedStudents: [],
      corporation: {},
      brand: {},
      school: {},
      course: {},
      tamaiClass: {},
    };
  },
  SET_CORPORATION(state, corporation) {
    state.groupStudent.corporation = corporation;
  },
  SET_BRAND(state, brand) {
    state.groupStudent.brand = brand;
  },
  SET_SCHOOL(state, school) {
    state.groupStudent.school = school;
  },
};

export const actions = {
  startImportingStudents({ commit, dispatch, getters }, data) {
    data = data.filter((item) => item['no']);
    data = data.map((item) => {
      item['error'] = null;
      item['status'] = STUDENT_STATUS.PENDING;
      item['figuredTeachingMaterialCodes'] = item['figuredTeachingMaterialCode'].split('_');
      return omit(item, ['figuredTeachingMaterialCode']);
    });
    dispatch('sidebar_progress/startImporting', null, { root: true });
    return StudentImporterService.collect(data)
      .then((response) => {
        commit('SET_STUDENTS', response);
        updateSidebarProgresses(getters, dispatch);
        return response;
      })
      .catch((error) => {
        dispatch('sidebar_progress/markImportingWithError', error.message, { root: true });
        return error.message;
      });
  },
  startEditingGroupStudents({ commit }, students) {
    commit('SET_SELECTED_STUDENTS', students);
  },
  finishEditingGroupStudents({ commit, state, dispatch, getters }) {
    state.groupStudent.selectedStudents.forEach((student) => {
      student.corporation = state.groupStudent.corporation;
      student.brand = state.groupStudent.brand;
      student.school = state.groupStudent.school;
      student.course = state.groupStudent.course;
      student.tamaiClass = state.groupStudent.tamaiClass;
      student.status = STUDENT_STATUS.READY;
    });
    commit('UPDATE_STUDENTS_FROM_SELECTED_STUDENTS', state.groupStudent.selectedStudents);
    commit('CLEAR_SELECTED_STUDENTS');

    updateSidebarProgresses(getters, dispatch);
  },
  removeStudents({ commit, getters, dispatch }, studentNos) {
    commit('REMOVE_STUDENTS', studentNos);
    updateSidebarProgresses(getters, dispatch);
  },
  finishImportingStudents({ commit, state, getters, dispatch }, payload) {
    let formattedStudents = state.students.map((student) => {
      let formattedStudent = cloneDeep(student);
      if (formattedStudent.corporation) {
        formattedStudent.corporationId = formattedStudent.corporation.id;
        formattedStudent = omit(formattedStudent, ['corporation']);
      }
      if (formattedStudent.brand) {
        formattedStudent.brandId = formattedStudent.brand.id;
        formattedStudent = omit(formattedStudent, ['brand']);
      }
      if (formattedStudent.school) {
        formattedStudent.schoolId = formattedStudent.school.id;
        formattedStudent = omit(formattedStudent, ['school']);
      }
      if (formattedStudent.course) {
        formattedStudent.courseId = formattedStudent.course.id;
        formattedStudent = omit(formattedStudent, ['course']);
      }
      if (formattedStudent.tamaiClass) {
        formattedStudent.tamaiClassId = formattedStudent.tamaiClass.id;
        formattedStudent = omit(formattedStudent, ['tamaiClass']);
      }
      formattedStudent = omit(formattedStudent, ['error', 'status']);
      if (payload.activationId) {
        switch (payload.activationId) {
          case '2':
            formattedStudent.preRegistration = true;
            break;
          case '3':
            formattedStudent.preRegistration = true;
            if (payload.preRegistrationMonth) {
              formattedStudent.preRegistrationDatetime = payload.preRegistrationMonth.value;
            }
            break;
          case '1':
          default:
            break;
        }
      }
      return formattedStudent;
    });

    return StudentImporterService.import(formattedStudents)
      .then((response) => {
        const importedStudents = response.data;
        const importedStudentIds = map(importedStudents, (student) => student.uid);

        let localImportedStudents = filter(state.students, (student) => importedStudentIds.includes(student.uid));
        localImportedStudents = uniqBy(localImportedStudents, (student) => student.uid);
        const localImportedStudentNos = map(localImportedStudents, (student) => student.no);

        const remainingStudents = filter(state.students, (student) => !localImportedStudentNos.includes(student.no));
        if (isEmpty(remainingStudents)) {
          commit('CLEAR_STUDENTS');
          dispatch('sidebar_progress/exitImporting', null, { root: true });
          return remainingStudents;
        } else {
          const invalidStudents = remainingStudents.map((student) => {
            student.status = 'ready';
            student.error = 'ID重複';
            return student;
          });
          commit('SET_STUDENTS', invalidStudents);
          updateSidebarProgresses(getters, dispatch);
          return invalidStudents;
        }
      })
      .catch((error) => {
        throw error;
      });
  },
  exitImportingStudents({ commit, dispatch }) {
    commit('CLEAR_STUDENTS');
    dispatch('sidebar_progress/exitImporting', null, { root: true });
  },
  markStudentNoError({ commit }, studentNo) {
    commit('MARK_STUDENT_NO_ERROR', studentNo);
  },
  setCorporation({ commit }, corporation) {
    commit('SET_CORPORATION', corporation);
  },
  setBrand({ commit }, brand) {
    commit('SET_BRAND', brand);
  },
  setSchool({ commit }, school) {
    commit('SET_SCHOOL', school);
  },
};

export const getters = {
  totalSelectedStudents: (state) => {
    return state.groupStudent.selectedStudents.length;
  },
  getStudentByNo: (state) => (no) => {
    return state.students.find((item) => item.no == no);
  },
  totalStudents: (state) => {
    return state.students.length;
  },
  totalPendingStudents: (state) => {
    return state.students.filter((student) => [STUDENT_STATUS.PENDING, STUDENT_STATUS.READY].includes(student.status))
      .length;
  },
  totalReadyStudents: (state) => {
    return state.students.filter((student) => student.status == STUDENT_STATUS.READY).length;
  },
  allStudentAlreadyPending: (state, getters) => {
    return getters.totalStudents > 0 && getters.totalPendingStudents == getters.totalStudents;
  },
  allStudentAlreadyReady: (state, getters) => {
    return getters.totalStudents > 0 && getters.totalReadyStudents == getters.totalStudents;
  },
};

const updateSidebarProgresses = (getters, dispatch) => {
  updateFirstSidebarProgress(getters, dispatch);
  updateSecondSidebarProgress(getters, dispatch);
};

const updateFirstSidebarProgress = (getters, dispatch) => {
  let payload = {
    value: getters.totalPendingStudents,
    done: getters.allStudentAlreadyPending,
  };
  dispatch('sidebar_progress/updatePendingImportProgress', payload, { root: true });
};

const updateSecondSidebarProgress = (getters, dispatch) => {
  let payload = {
    value: getters.totalReadyStudents,
    done: getters.allStudentAlreadyReady,
  };
  dispatch('sidebar_progress/updateReadyImportProgress', payload, { root: true });
};

const STUDENT_STATUS = {
  PENDING: 'pending',
  READY: 'ready',
};
